import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  RefreshCw,
  Filter,
  Brain,
  Target,
  Users,
  Sparkles,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import InstagramCarousel from "./InstagramCarousel";
import ProcessingStatusBar from "./ProcessingStatusBar";
import { feedAPI, interactionsAPI } from "../services/api";
import { useProcessing } from "../contexts/ProcessingContext";
import useInstanceStore from "../stores/instanceStore";
import { useChapter } from "../contexts/ChapterContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const FeedFilterDropdown = ({ currentFilter, onFilterChange, instanceId }) => {
  const { t } = useTranslation();
  const [failedQuizCount, setFailedQuizCount] = useState(0);

  useEffect(() => {
    // Get count of failed quiz items for Mastery Mode
    const getFailedQuizCount = async () => {
      try {
        // This would be an API call to get failed quiz items
        // For now, using mock data
        setFailedQuizCount(Math.floor(Math.random() * 5) + 1);
      } catch (error) {
        console.error("Error getting failed quiz count:", error);
      }
    };

    if (instanceId) {
      getFailedQuizCount();
    }
  }, [instanceId]);

  const filterOptions = [
    {
      value: "all",
      label: `🧠 ${t('feed.filters.all')}`,
      description: t('feed.filters.description.all'),
      icon: <Sparkles className="w-3 h-3" />,
    },
    {
      value: "mastery",
      label: `🎯 ${t('feed.filters.mastery')}`,
      description: t('feed.filters.description.mastery', { count: failedQuizCount }),
      icon: <Target className="w-3 h-3" />,
      badge: failedQuizCount > 0 ? failedQuizCount : null,
    },
    {
      value: "characters",
      label: `👥 ${t('feed.filters.characters')}`,
      description: t('feed.filters.description.characters'),
      icon: <Users className="w-3 h-3" />,
    },
    {
      value: "concepts",
      label: `💡 ${t('feed.filters.concepts')}`,
      description: t('feed.filters.description.concepts'),
      icon: <Brain className="w-3 h-3" />,
    },
  ];

  return (
    <Select value={currentFilter} onValueChange={onFilterChange}>
      <SelectTrigger className="w-20 h-8 text-xs">
        <div className="flex items-center gap-1">
          {filterOptions.find((opt) => opt.value === currentFilter)?.icon}
          <span className="truncate">
            {filterOptions
              .find((opt) => opt.value === currentFilter)
              ?.label.split(" ")[1] || "All"}
          </span>
          {filterOptions.find((opt) => opt.value === currentFilter)?.badge && (
            <span className="ml-auto bg-red-500 text-white text-xs px-1 py-0.5 rounded-full">
              {filterOptions.find((opt) => opt.value === currentFilter).badge}
            </span>
          )}
        </div>
      </SelectTrigger>
      <SelectContent>
        {filterOptions.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                {option.icon}
                <div>
                  <div className="font-medium text-sm">{option.label}</div>
                  <div className="text-xs text-gray-500">
                    {option.description}
                  </div>
                </div>
              </div>
              {option.badge && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full ml-2">
                  {option.badge}
                </span>
              )}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const StoryPreviewBar = ({
  instanceId,
  onStoryClick,
  stories = [],
  entities = [],
}) => {
  if (!stories || stories.length === 0) return null;

  return (
    <div className="bg-white border-b border-gray-200 p-4">
      <div className="flex gap-4 overflow-x-auto scrollbar-hide">
        {stories.map((story) => {
          const entity = entities.find((e) => e.id === story.entity_id);
          if (!entity) return null;

          return (
            <div
              key={story.id}
              onClick={() => onStoryClick(entity, [story])}
              className="flex-shrink-0 cursor-pointer"
            >
              <div
                className={`w-16 h-16 rounded-full p-0.5 ${
                  story.viewed_by_user
                    ? "bg-gray-300"
                    : "bg-gradient-to-r from-purple-500 to-pink-500"
                }`}
              >
                <div className="w-full h-full rounded-full bg-white p-0.5">
                  <img
                    src={entity.avatar_url}
                    alt={entity.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                </div>
              </div>
              <p className="text-xs text-center mt-1 w-16 truncate">
                {entity.name.split(" ")[0]}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EnhancedFeed = ({
  instanceId: propsInstanceId,
  onProfileClick,
  onStoryClick,
  onOpenSaveModal,
  savedPostIds = [],
  stories,
  entities,
  setShowSettings,
  setCurrentTab,
}) => {
  const { t } = useTranslation();
  const {
    selectedInstanceId,
    setLoading: setStoreLoading,
    setError: setStoreError,
  } = useInstanceStore();
  const { currentChapter, updateChapterProgress, navigateToChapter } =
    useChapter();
  const instanceId = propsInstanceId || selectedInstanceId;
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [filter, setFilter] = useState("all");
  // Use currentChapter from context, with local override for "all chapters" view
  const [showAllChapters, setShowAllChapters] = useState(true); // null = all chapters
  const [availableChapters, setAvailableChapters] = useState([]);
  const [sortOrder, setSortOrder] = useState(() => {
    try {
      return localStorage.getItem("feedSortOrder") || "source_order";
    } catch {
      return "source_order";
    }
  }); // source_order or creation_time
  const [error, setError] = useState(null);
  const { getAllJobs } = useProcessing();

  const loadingRef = useRef(false);
  const observerRef = useRef();
  const feedContainerRef = useRef();
  const suppressChapterScrollRef = useRef(false);

  // Load posts
  const loadPosts = useCallback(
    async (cursor = null, isRefresh = false) => {
      if (loadingRef.current) return;

      // Early return if no instanceId
      if (!instanceId) {
        setLoading(false);
        setPosts([]);
        return;
      }

      loadingRef.current = true;

      try {
        setError(null);
        if (isRefresh) {
          setRefreshing(true);
          setPosts([]);
        } else if (!cursor) {
          setLoading(true);
        }

        const params = {
          instanceId,
          cursor,
          limit: 50,
          type: "post",
          chapterIndex: showAllChapters ? null : currentChapter, // Pass current chapter from context or null for all
          sortBy: sortOrder, // Use selected sort order (source_order or creation_time)
        };
        const response = await feedAPI.getFeed(
          params.instanceId,
          params.cursor,
          params.limit,
          params.type,
          params.chapterIndex,
          params.sortBy
        );

        if (response.posts) {
          // Phase 3: Apply strict validation first - only show posts with 2-5 slides + concept + quiz
          const validatePost = (post) => {
            const slides = post.slides || [];
            const slidesCount = slides.length;
            const hasConcept = slides.some((slide) => slide.type === "concept");
            const hasQuiz = slides.some((slide) => slide.type === "quiz");

            const isValid =
              slidesCount >= 2 && slidesCount <= 5 && hasConcept && hasQuiz;

            if (!isValid) {
            }

            return isValid;
          };

          // Apply content validation
          let validatedPosts = response.posts.filter(validatePost);

          // Apply user filter on top of validated posts
          let filteredPosts = validatedPosts;

          if (filter === "mastery") {
            // Filter to show posts with failed quiz items
            filteredPosts = filteredPosts.filter(
              (post) =>
                post.slides &&
                post.slides.some((slide) => slide.type === "quiz")
            );
          } else if (filter === "characters") {
            filteredPosts = filteredPosts.filter(
              (post) => post.entity && post.entity.type === "character"
            );
          } else if (filter === "concepts") {
            filteredPosts = filteredPosts.filter(
              (post) =>
                post.slides &&
                post.slides.some(
                  (slide) =>
                    slide.type === "concept" || slide.type === "analysis"
                )
            );
          }

          // NOTE: Backend now handles sorting (server/src/routes/feed.ts).
          // We trust the backend order and only apply it directly.
          // No client-side re-sorting to avoid conflicts.

          // Mark posts as saved if their ID is in savedPostIds
          filteredPosts = filteredPosts.map((post) => ({
            ...post,
            saved_by_user: savedPostIds.includes(post.id),
          }));

          if (isRefresh || !cursor) {
            setPosts(filteredPosts);
          } else {
            setPosts((prev) => [...prev, ...filteredPosts]);
          }

          setHasMore(response.has_more);
          setNextCursor(response.next_cursor);
        }
      } catch (error) {
        console.error("Error loading posts:", error);
        const errorMessage = t('feed.posts_load_failed');
        setError(errorMessage);
        setStoreError(errorMessage);
        toast.error(t('feed.posts_load_error'));
      } finally {
        setLoading(false);
        setRefreshing(false);
        loadingRef.current = false;
      }
    },
    [instanceId, filter]
  );

  // Subscribe to instance changes and trigger feed updates
  useEffect(() => {
    const unsubscribe = useInstanceStore.subscribe(
      (state) => state.selectedInstanceId,
      (newInstanceId) => {
        if (newInstanceId && newInstanceId !== instanceId) {
          console.log(
            "🔄 Instance changed, refreshing feed for:",
            newInstanceId
          );
          // Clear posts immediately for instant feedback
          setPosts([]);
          setLoading(true);
          // Remove delay - load immediately
          loadPosts(null, true);
        }
      }
    );

    return unsubscribe;
  }, [instanceId, loadPosts]);

  // Initial load and filter changes
  useEffect(() => {
    if (instanceId) {
      setStoreLoading(true);
      loadPosts(null, true).finally(() => setStoreLoading(false));
    }
  }, [
    instanceId,
    filter,
    currentChapter,
    showAllChapters,
    sortOrder,
    loadPosts,
    setStoreLoading,
  ]);

  // Persist sort order preference
  useEffect(() => {
    try {
      localStorage.setItem("feedSortOrder", sortOrder);
    } catch {}
  }, [sortOrder]);

  // Update posts when savedPostIds changes (when user saves/unsaves posts)
  useEffect(() => {
    if (posts.length === 0) return;

    setPosts((prevPosts) =>
      prevPosts.map((post) => ({
        ...post,
        saved_by_user: savedPostIds.includes(post.id),
      }))
    );
  }, [savedPostIds]);

  // Extract chapters from existing posts data instead of separate API call
  useEffect(() => {
    if (!instanceId || posts.length === 0) return;

    const uniqueChapters = [
      ...new Set(
        posts
          .map((post) => post.chapter_index)
          .filter(
            (chapterIndex) =>
              chapterIndex !== null && chapterIndex !== undefined
          )
      ),
    ].sort((a, b) => a - b);

    if (uniqueChapters.length > 0) {
      setAvailableChapters(uniqueChapters);
      console.log(
        `📚 Extracted ${uniqueChapters.length} chapters from loaded posts:`,
        uniqueChapters
      );
    }
  }, [instanceId, posts]);

  // Check for new processed content - optimized with throttling
  useEffect(() => {
    const processingJobs = getAllJobs();
    const recentlyCompleted = processingJobs.find(
      (job) =>
        job.stage === "completed" &&
        job.lastUpdate &&
        Date.now() - job.lastUpdate < 10000 // Within last 10 seconds
    );

    if (recentlyCompleted && !loadingRef.current) {
      console.log("🔄 New content processed, refreshing feed...");
      loadPosts(null, true);
    }
  }, [getAllJobs, loadPosts]);

  // Chapter navigation - scroll to chapter when currentChapter changes
  useEffect(() => {
    if (!currentChapter || !feedContainerRef.current || posts.length === 0)
      return;

    // Don't scroll if suppressed (e.g., during save/like actions)
    if (suppressChapterScrollRef.current) return;

    const scrollToChapter = () => {
      // Find the first post of the target chapter
      const targetChapterPost = posts.find(
        (post) =>
          post.chapter_index === currentChapter ||
          post.chapter_number === currentChapter
      );

      if (targetChapterPost) {
        // Find the DOM element for this post
        const postElement = feedContainerRef.current.querySelector(
          `[data-post-id="${targetChapterPost.id}"]`
        );
        if (postElement) {
          // Smooth scroll to the post with some offset for better UX
          const yOffset = -100; // Adjust for fixed headers
          const y =
            postElement.getBoundingClientRect().top +
            window.pageYOffset +
            yOffset;

          window.scrollTo({
            top: y,
            behavior: "smooth",
          });

          console.log(
            `📍 Scrolled to chapter ${currentChapter} (post: ${targetChapterPost.id})`
          );
          return;
        }
      }

      // If no specific post found, look for chapter separator
      const chapterSeparator = feedContainerRef.current.querySelector(
        `[data-chapter="${currentChapter}"]`
      );
      if (chapterSeparator) {
        const yOffset = -100;
        const y =
          chapterSeparator.getBoundingClientRect().top +
          window.pageYOffset +
          yOffset;

        window.scrollTo({
          top: y,
          behavior: "smooth",
        });

        console.log(`📍 Scrolled to chapter ${currentChapter} separator`);
      } else {
        console.warn(`⚠️ Could not find content for chapter ${currentChapter}`);

        // Try to load content specifically for this chapter from backend
        console.log(
          `🔄 Attempting to load chapter ${currentChapter} content from backend`
        );

        // Load posts for this specific chapter only
        const loadSpecificChapter = async () => {
          try {
            setLoading(true);
            const params = {
              instanceId,
              cursor: null,
              limit: 50,
              type: "post",
              chapterIndex: currentChapter, // Load only this specific chapter
              sortBy: sortOrder,
            };

            const response = await feedAPI.getFeed(
              params.instanceId,
              params.cursor,
              params.limit,
              params.type,
              params.chapterIndex,
              params.sortBy
            );

            if (response.posts && response.posts.length > 0) {
              console.log(`✅ Found content for chapter ${currentChapter}`);

              // Add new chapter content to existing posts
              const newValidatedPosts = response.posts.filter(validatePost);
              setPosts((prevPosts) => {
                // Remove duplicates and add new posts, then sort
                const existingIds = new Set(prevPosts.map((p) => p.id));
                const uniqueNewPosts = newValidatedPosts.filter(
                  (p) => !existingIds.has(p.id)
                );
                const combinedPosts = [...prevPosts, ...uniqueNewPosts];

                const sortBySource = (x, y) => {
                  const chapterA = Number.isFinite(x.chapter_index)
                    ? x.chapter_index
                    : Number.isFinite(x.chapter_number)
                    ? x.chapter_number
                    : 0;
                  const chapterB = Number.isFinite(y.chapter_index)
                    ? y.chapter_index
                    : Number.isFinite(y.chapter_number)
                    ? y.chapter_number
                    : 0;
                  if (chapterA !== chapterB) return chapterA - chapterB;
                  const pageA =
                    x.page_number ||
                    x.source_page_start ||
                    x.position_data?.estimated_page ||
                    999;
                  const pageB =
                    y.page_number ||
                    y.source_page_start ||
                    y.position_data?.estimated_page ||
                    999;
                  if (pageA !== pageB) return pageA - pageB;
                  const lineA =
                    x.source_line_start ||
                    x.line_number ||
                    x.position_data?.line ||
                    0;
                  const lineB =
                    y.source_line_start ||
                    y.line_number ||
                    y.position_data?.line ||
                    0;
                  return lineA - lineB;
                };

                const sortByLatest = (x, y) => {
                  const ta = new Date(
                    x.created_at || x.updated_at || 0
                  ).getTime();
                  const tb = new Date(
                    y.created_at || y.updated_at || 0
                  ).getTime();
                  return tb - ta;
                };

                return combinedPosts.sort(
                  sortOrder === "creation_time" ? sortByLatest : sortBySource
                );
              });

              // Retry scrolling after content is loaded
              setTimeout(scrollToChapter, 500);
            } else {
              console.log(
                `❌ No content exists for chapter ${currentChapter} in database`
              );
              toast.error(
                `Chapter ${currentChapter} content is not available yet`
              );
            }
          } catch (error) {
            console.error("Error loading specific chapter:", error);
            toast.error(`Failed to load chapter ${currentChapter}`);
          } finally {
            setLoading(false);
          }
        };

        loadSpecificChapter();
      }
    };

    // Delay scrolling slightly to ensure DOM is updated
    const timer = setTimeout(scrollToChapter, 100);
    return () => clearTimeout(timer);
  }, [currentChapter, posts]);

  // Infinite scroll with automatic next chapter loading
  const lastPostElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          if (hasMore && nextCursor) {
            // Regular infinite scroll with cursor
            loadPosts(nextCursor);
          } else if (!hasMore && posts.length > 0) {
            // No more cursor-based posts, try loading next chapter automatically
            const maxChapter = Math.max(
              ...posts.map((p) =>
                Number.isFinite(p.chapter_index)
                  ? p.chapter_index
                  : Number.isFinite(p.chapter_number)
                  ? p.chapter_number
                  : 0
              )
            );
            const nextChapter = maxChapter + 1;

            console.log(`🔄 Auto-loading next chapter: ${nextChapter}`);

            // Load next chapter automatically
            const loadNextChapter = async () => {
              try {
                const params = {
                  instanceId,
                  cursor: null,
                  limit: 50,
                  type: "post",
                  chapterIndex: nextChapter,
                  sortBy: sortOrder,
                };

                const response = await feedAPI.getFeed(
                  params.instanceId,
                  params.cursor,
                  params.limit,
                  params.type,
                  params.chapterIndex,
                  params.sortBy
                );

                if (response.posts && response.posts.length > 0) {
                  console.log(`✅ Auto-loaded chapter ${nextChapter}`);

                  const validatePost = (post) => {
                    const slides = post.slides || [];
                    const slidesCount = slides.length;
                    const hasConcept = slides.some(
                      (slide) => slide.type === "concept"
                    );
                    const hasQuiz = slides.some(
                      (slide) => slide.type === "quiz"
                    );
                    return (
                      slidesCount >= 2 &&
                      slidesCount <= 5 &&
                      hasConcept &&
                      hasQuiz
                    );
                  };

                  const newValidatedPosts = response.posts.filter(validatePost);
                  if (newValidatedPosts.length > 0) {
                    setPosts((prevPosts) => {
                      const existingIds = new Set(prevPosts.map((p) => p.id));
                      const uniqueNewPosts = newValidatedPosts.filter(
                        (p) => !existingIds.has(p.id)
                      );
                      const combined = [...prevPosts, ...uniqueNewPosts];
                      const sortBySource = (x, y) => {
                        const chapterA = Number.isFinite(x.chapter_index)
                          ? x.chapter_index
                          : Number.isFinite(x.chapter_number)
                          ? x.chapter_number
                          : 0;
                        const chapterB = Number.isFinite(y.chapter_index)
                          ? y.chapter_index
                          : Number.isFinite(y.chapter_number)
                          ? y.chapter_number
                          : 0;
                        if (chapterA !== chapterB) return chapterA - chapterB;
                        const pageA =
                          x.page_number ||
                          x.source_page_start ||
                          x.position_data?.estimated_page ||
                          999;
                        const pageB =
                          y.page_number ||
                          y.source_page_start ||
                          y.position_data?.estimated_page ||
                          999;
                        if (pageA !== pageB) return pageA - pageB;
                        const lineA =
                          x.source_line_start ||
                          x.line_number ||
                          x.position_data?.line ||
                          0;
                        const lineB =
                          y.source_line_start ||
                          y.line_number ||
                          y.position_data?.line ||
                          0;
                        return lineA - lineB;
                      };
                      const sortByLatest = (x, y) => {
                        const ta = new Date(
                          x.created_at || x.updated_at || 0
                        ).getTime();
                        const tb = new Date(
                          y.created_at || y.updated_at || 0
                        ).getTime();
                        return tb - ta;
                      };
                      return combined.sort(
                        sortOrder === "creation_time"
                          ? sortByLatest
                          : sortBySource
                      );
                    });
                    setHasMore(true); // Enable continued scrolling
                  }
                }
              } catch (error) {
                console.error("Auto-load next chapter failed:", error);
              }
            };

            loadNextChapter();
          }
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [
      loading,
      hasMore,
      nextCursor,
      loadPosts,
      posts,
      instanceId,
      sortOrder,
      feedAPI,
    ]
  );

  const handleLike = async (postId, liked) => {
    try {
      await interactionsAPI.toggleLike(postId, liked);
      setPosts((prev) =>
        prev.map((post) =>
          post.id === postId
            ? {
                ...post,
                liked_by_user: liked,
                likes: liked ? post.likes + 1 : Math.max(0, post.likes - 1),
              }
            : post
        )
      );
    } catch (error) {
      console.error("Error toggling like:", error);
    }
  };

  const handleSave = async (postId, saved) => {
    // Suppress scroll during save/unsave actions
    suppressChapterScrollRef.current = true;

    try {
      // Find the post object
      const post = posts.find((p) => p.id === postId);
      if (!post) return;

      if (saved) {
        // Only open modal when saving
        if (onOpenSaveModal) {
          onOpenSaveModal(post);
        } else {
          // Fallback: use old behavior if modal not available
          await interactionsAPI.toggleSave(postId, saved, instanceId);
          setPosts((prev) =>
            prev.map((p) =>
              p.id === postId
                ? {
                    ...p,
                    saved_by_user: saved,
                    saves: saved ? p.saves + 1 : Math.max(0, p.saves - 1),
                  }
                : p
            )
          );
          toast.success(t('feed.post_saved_success'));
        }
      } else {
        // Unsave: remove from all collections directly
        await interactionsAPI.removeFromAllCollections(postId);
        setPosts((prev) =>
          prev.map((p) =>
            p.id === postId
              ? { ...p, saved_by_user: false, saves: Math.max(0, p.saves - 1) }
              : p
          )
        );
        toast.success(t('feed.post_removed_from_saved'));
      }
    } catch (error) {
      console.error("Error toggling save:", error);
      toast.error(
        saved ? "Failed to save post" : "Failed to remove from saved"
      );
    } finally {
      // Re-enable scroll after a short delay
      setTimeout(() => {
        suppressChapterScrollRef.current = false;
      }, 300);
    }
  };

  const handleComment = (postId) => {
    // Navigate to comments or open comment modal
    toast.info(t('feed.comment_coming_soon'));
  };

  const handlePostView = (
    progressPercentage,
    viewedPosts,
    totalPosts,
    chapterNumber = null
  ) => {
    console.log(
      `📊 Post viewed - Progress: ${progressPercentage}%, Viewed: ${viewedPosts}/${totalPosts}`
    );

    // Update chapter progress in context if chapter info is available
    if (chapterNumber && totalPosts > 0) {
      updateChapterProgress(chapterNumber, viewedPosts, totalPosts);
    }

    // This will trigger instance card updates through the instance store/context
  };

  const handleRefresh = () => {
    loadPosts(null, true);
  };

  // Show no instance message if no instanceId provided
  if (!instanceId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8">
        <div className="text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <Sparkles className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t('feed.no_content_title')}
          </h2>
          <p className="text-gray-600 mb-4">
            {t('feed.no_content_desc')}
          </p>
          <p className="text-xs text-gray-500">
            {t('feed.go_to_profile')}
          </p>
        </div>
      </div>
    );
  }

  if (loading && posts.length === 0) {
    return (
      <div className="space-y-4 p-4">
        {/* Story previews skeleton */}
        <div className="flex gap-4 overflow-x-auto">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex-shrink-0">
              <div className="w-16 h-16 bg-gray-200 rounded-full animate-pulse"></div>
              <div className="w-12 h-3 bg-gray-200 rounded mt-1 animate-pulse"></div>
            </div>
          ))}
        </div>

        {/* Posts skeleton */}
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-lg border border-gray-200 overflow-hidden"
          >
            <div className="flex items-center gap-3 p-4">
              <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
              <div className="flex-1">
                <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-16 h-3 bg-gray-200 rounded mt-1 animate-pulse"></div>
              </div>
            </div>
            <div className="w-full h-64 bg-gray-200 animate-pulse"></div>
            <div className="p-4 space-y-2">
              <div className="w-3/4 h-4 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-1/2 h-3 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div ref={feedContainerRef} className="bg-gray-50 min-h-screen">
      {/* Compact Filter Controls - Top Left */}
      {/* <div className="bg-white p-2 pl-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-20">
              <FeedFilterDropdown
                currentFilter={filter}
                onFilterChange={setFilter}
                instanceId={instanceId}
              />
            </div>

            <div className="w-32">
              <Select value={sortOrder} onValueChange={setSortOrder}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="source_order">📖 Story Order</SelectItem>
                  <SelectItem value="creation_time">⏰ Latest First</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-1"
          >
            <RefreshCw
              className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
            />
          </Button>
        </div>
      </div> */}

      {/* Story Preview Bar */}
      {/* <StoryPreviewBar
        instanceId={instanceId}
        onStoryClick={onStoryClick}
        stories={stories}
        entities={entities}
      /> */}

      {/* Feed Content */}
      <div className="pb-20">
        {error && (
          <div className="p-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                className="mt-2"
              >
                Try Again
              </Button>
            </div>
          </div>
        )}

        {posts.length === 0 && !loading && !error && (
          <div className="text-center py-12 px-4">
            <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('feed.status.no_validated_posts')}
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              {filter === "all"
                ? t('feed.status.no_validated_desc')
                : filter === "mastery"
                ? t('feed.status.no_mastery_desc')
                : t('feed.status.no_filter_desc', { filter })}
            </p>
            {filter !== "all" && (
              <Button variant="outline" onClick={() => setFilter("all")}>
                {t('feed.status.show_all')}
              </Button>
            )}
          </div>
        )}

        {/* Processing Status as First Feed Post */}
        <div className="p-4">
          <ProcessingStatusBar asFeedPost={true} />
        </div>

        {posts.map((post, index) => {
          const isLast = index === posts.length - 1;

          // Check if this is the start of a new chapter
          const prevPost = index > 0 ? posts[index - 1] : null;
          const isNewChapter =
            prevPost && post.chapter_index !== prevPost.chapter_index;

          return (
            <div key={post.id}>
              {/* Chapter separator */}
              {isNewChapter && (
                <div className="px-4 py-2" data-chapter={post.chapter_index}>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-blue-200"></div>
                    <span className="text-blue-600 text-sm font-medium bg-blue-50 px-3 py-1 rounded-full">
                      {post.chapter_title &&
                      /(introduction|preface|foreword|abstract|prologue|overview)/i.test(
                        post.chapter_title
                      )
                        ? t('feed.status.intro_chapter')
                        : post.chapter_title || `${t('navigation.chapters')} ${post.chapter_index}`}
                    </span>
                    <div className="flex-1 h-px bg-blue-200"></div>
                  </div>
                </div>
              )}

              {/* First post also needs chapter separator */}
              {index === 0 && Number.isFinite(post.chapter_index) && (
                <div className="px-4 py-2" data-chapter={post.chapter_index}>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-blue-200"></div>
                    <span className="text-blue-600 text-sm font-medium bg-blue-50 px-3 py-1 rounded-full">
                      {post.chapter_title &&
                      /(introduction|preface|foreword|abstract|prologue|overview)/i.test(
                        post.chapter_title
                      )
                        ? t('feed.status.intro_chapter')
                        : post.chapter_title || `${t('navigation.chapters')} ${post.chapter_index}`}
                    </span>
                    <div className="flex-1 h-px bg-blue-200"></div>
                  </div>
                </div>
              )}

              <div
                ref={isLast ? lastPostElementRef : null}
                className="p-4"
                data-post-id={post.id}
                data-chapter={post.chapter_index || post.chapter_number}
              >
                
                <InstagramCarousel
                  setShowSettings={setShowSettings}
                  setCurrentTab={setCurrentTab}
                  post={post}
                  entity={post.entity}
                  onLike={handleLike}
                  onSave={handleSave}
                  onComment={handleComment}
                  onProfileClick={onProfileClick}
                  onPostDeleted={(deletedPostId) => {
                    setPosts((prevPosts) =>
                      prevPosts.filter((p) => p.id !== deletedPostId)
                    );
                  }}
                />
              </div>
            </div>
          );
        })}

        {/* Loading more indicator */}
        {hasMore && nextCursor && (
          <div className="p-4">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          </div>
        )}

        {/* End of feed indicator with lazy loading */}
        {!hasMore && posts.length > 0 && (
          <div className="text-center py-8 px-4">
            <p className="text-gray-500 text-sm mb-4">
              {t('feed.status.reached_chapter', { count: Math.max(
                ...posts.map((p) =>
                  Number.isFinite(p.chapter_index)
                    ? p.chapter_index
                    : Number.isFinite(p.chapter_number)
                    ? p.chapter_number
                    : 0
                )
              )})}
            </p>
            <div className="space-y-2">
              <Button
                variant="default"
                size="sm"
                onClick={async () => {
                  const maxChapter = Math.max(
                    ...posts.map((p) =>
                      Number.isFinite(p.chapter_index)
                        ? p.chapter_index
                        : Number.isFinite(p.chapter_number)
                        ? p.chapter_number
                        : 0
                    )
                  );
                  const nextChapter = maxChapter + 1;

                  console.log(`🔄 Loading next chapter: ${nextChapter}`);
                  setLoading(true);

                  try {
                    // Try to load the next chapter
                    const params = {
                      instanceId,
                      cursor: null,
                      limit: 50,
                      type: "post",
                      chapterIndex: nextChapter,
                      sortBy: sortOrder,
                    };

                    const response = await feedAPI.getFeed(
                      params.instanceId,
                      params.cursor,
                      params.limit,
                      params.type,
                      params.chapterIndex,
                      params.sortBy
                    );

                    if (response.posts && response.posts.length > 0) {
                      console.log(
                        `✅ Loaded chapter ${nextChapter} with ${response.posts.length} posts`
                      );

                      const newValidatedPosts =
                        response.posts.filter(validatePost);
                      setPosts((prevPosts) => {
                        const existingIds = new Set(prevPosts.map((p) => p.id));
                        const uniqueNewPosts = newValidatedPosts.filter(
                          (p) => !existingIds.has(p.id)
                        );
                        return [...prevPosts, ...uniqueNewPosts].sort(
                          (a, b) => {
                            const chapterA = Number.isFinite(a.chapter_index)
                              ? a.chapter_index
                              : Number.isFinite(a.chapter_number)
                              ? b.chapter_number
                              : 0;
                            const chapterB = Number.isFinite(b.chapter_index)
                              ? b.chapter_index
                              : Number.isFinite(b.chapter_number)
                              ? b.chapter_number
                              : 0;
                            if (chapterA !== chapterB)
                              return chapterA - chapterB; // Handles negative intro chapters

                            const pageA =
                              a.page_number ||
                              a.source_page_start ||
                              a.position_data?.estimated_page ||
                              999;
                            const pageB =
                              b.page_number ||
                              b.source_page_start ||
                              b.position_data?.estimated_page ||
                              999;
                            if (pageA !== pageB) return pageA - pageB;

                            const lineA =
                              a.source_line_start ||
                              a.line_number ||
                              a.position_data?.line ||
                              0;
                            const lineB =
                              b.source_line_start ||
                              b.line_number ||
                              b.position_data?.line ||
                              0;
                            return lineA - lineB;
                          }
                        );
                      });

                      if (newValidatedPosts.length > 0) {
                        setHasMore(true);
                        toast.success(`${t('feed.loaded_chapter', { count: nextChapter })}`);
                      } else {
                        toast.info(
                          `Chapter ${nextChapter} content exists but doesn't meet validation criteria`
                        );
                      }
                    } else {
                      console.log(
                        `❌ No more content available after chapter ${maxChapter}`
                      );
                      toast.info(
                        `${t('feed.no_more_chapters', { count: maxChapter })}`
                      );
                    }
                  } catch (error) {
                    console.error("Error loading next chapter:", error);
                    toast.error(`${t('feed.chapter_load_failed', { count: nextChapter })}`);
                  } finally {
                    setLoading(false);
                  }
                }}
                className="mr-2"
              >
                📖 {t('feed.status.load_next_chapter')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleRefresh}>
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('feed.status.refresh_all')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedFeed;
